// @file Define functions to make API calls
import getCsrfToken from '@@/bits/csrf_token'
import window, { uid } from '@@/bits/global'
import { sleep } from '@@/bits/sleep'
import { getPathWithLeadingSlash } from '@@/bits/url'
import { apiFetch } from '@padlet/arvo'
import { FetchError, fetchJson, FetchOptions } from '@padlet/fetch'
let useArvo = true

interface ApiFetchOptions {
  useArvo: boolean
}

function configureApiFetch(options: ApiFetchOptions): void {
  useArvo = options.useArvo
}

const apiFetchJson = async (url: string, options: FetchOptions = {}): Promise<any> => {
  const commonOptions: FetchOptions = {
    headers: {
      Accept: 'application/json, application/vnd.api+json',
      'Content-Type': 'application/json; charset=utf-8',
      'X-CSRF-Token': getCsrfToken(),
      'X-UID': window?.ww?.uid || uid,
    },
  }

  // Merge `commonOptions.headers` with `options.headers` so callers don't have to provide
  // the common headers when they add custom headers.
  if (typeof options.headers === 'object') {
    options.headers = { ...commonOptions.headers, ...options.headers }
  }

  // Workaround for Firestore banned in China,
  // use the direct way to fetch instead.
  if (!useArvo) {
    return fetchJson(url, { ...commonOptions, ...options })
  }
  const pathWithLeadingSlash = getPathWithLeadingSlash(url)
  return apiFetch<any>(pathWithLeadingSlash, commonOptions, options)
}

const fetchJsonWithRetries = async (url: string, options: FetchOptions = {}, maxTries = 4): Promise<any> => {
  try {
    return await apiFetchJson(url, options)
  } catch (error) {
    if (maxTries === 1 || error.status < 500) throw error
    await sleep(10, 50)
    return fetchJsonWithRetries(url, options, maxTries - 1)
  }
}

export { configureApiFetch, FetchError, apiFetchJson as fetchJson, fetchJsonWithRetries }
